import type { ProcessedCourse } from "@/common/course/types";
import { ProgramType } from "@/common/program/types";
import { PAID_MODES } from "@/lib/course/utils/constants";

/**
 * Allowed `product_type` values for PDP Product Viewed (from product / route).
 */
export type SegmentProductViewItemType =
  | "course"
  | "professional-cert"
  | "xseries"
  | "micromasters"
  | "microbachelors"
  | "executive-education";

/**
 * Derives `product_type` for program PDPs from `ProgramType` and path (path used when enum is ambiguous).
 */
export function segmentItemTypeFromProgram(
  programType: ProgramType,
  path: string,
): SegmentProductViewItemType {
  switch (programType) {
    case ProgramType.PROFESSIONALCERTIFICATE:
    case ProgramType.PROFESSIONALPROGRAM:
      return "professional-cert";
    case ProgramType.XSERIES:
      return "xseries";
    case ProgramType.MICROMASTERS:
      return "micromasters";
    case ProgramType.MICROBACHELORS:
      return "microbachelors";
    default: {
      const p = path.toLowerCase();
      if (p.includes("professional-certificate")) return "professional-cert";
      if (p.includes("xseries")) return "xseries";
      if (p.includes("micromasters")) return "micromasters";
      if (p.includes("microbachelors")) return "microbachelors";
      return "professional-cert";
    }
  }
}

/** Course run key (e.g. `course-v1:IBM+AI0101EN+1T2025`). */
export function getCourseSku(course: ProcessedCourse): string | undefined {
  const key = course.activeCourseRun?.key;
  return key?.trim() || undefined;
}

/** Human-readable category (subject name) */
export function getCourseCategoryDisplay(
  course: ProcessedCourse,
): string | undefined {
  const fromSubject = course.subjects?.[0]?.name?.trim();
  if (fromSubject) return fromSubject;
  const en = course.subjectEn?.trim();
  if (en) return en;
  const es = course.subjectEs?.trim();
  return es || undefined;
}

/** Primary partner key for Segment (e.g. `HarvardX`). */
export function getPrimaryPartnerBrandKey(
  partners: ProcessedCourse["partners"] | null | undefined,
): string | undefined {
  if (!partners?.length) return undefined;
  const sorted = [...partners].sort(
    (a, b) => (a.sortValue ?? 0) - (b.sortValue ?? 0),
  );
  const key = sorted[0]?.key?.trim() || partners[0]?.key?.trim();
  return key || undefined;
}

/**
 * List price, optional sale price, and currency from paid seats on the active run.
 * When only one paid price exists, only `price` is set (no separate sale).
 */
export function getProcessedCoursePriceFields(course: ProcessedCourse): {
  price?: number;
  sale_price?: number;
  currency?: string;
} {
  const run = course.activeCourseRun;
  if (!run?.seats?.length) return {};

  const paidSeats = run.seats.filter((s) => PAID_MODES.has(s.type));
  if (!paidSeats.length) return {};

  const prices = paidSeats
    .map((s) => s.price)
    .filter((p): p is number => typeof p === "number" && !Number.isNaN(p));
  if (!prices.length) return {};

  const maxP = Math.max(...prices);
  const minP = Math.min(...prices);
  const currency = (paidSeats[0].currencyId || "usd").toLowerCase();

  if (maxP === minP) {
    return { price: maxP, currency };
  }
  return { price: maxP, sale_price: minP, currency };
}
